<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { extend } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import '../../lib/r7api'
import { useApi, defaultConfig } from '../../composables/api'
import { checkIfEditable } from '.'
import { getEditorConfig } from './view'
import { markEditorLeft } from './editorLeave'
const props = defineProps({
  dockey: {
    type: String,
    required: true,
  },
  mention: String,
  editable: {
    type: Boolean,
    default: true,
  },
})
const route = useRoute()
const router = useRouter()
const unlockToken = computed(() => {
  const raw = route.query.unlock
  return typeof raw === 'string' && raw ? raw : ''
})
/** secure_filename просмотрщика, с которого открыли редактор. */
const fromFilename = computed(() => {
  const raw = route.query.from
  return typeof raw === 'string' && raw ? raw : ''
})
const { user } = storeToRefs(defaultConfig.store_user?.())

/** Язык Р7 = переключатель LTMS (ltms-locale). Без выбора там уже дефолт глобала. */
function editorUiLang() {
  try {
    const raw = String(window.localStorage.getItem('ltms-locale') || '')
      .replace(/^"|"$/g, '')
      .trim()
      .toLowerCase()
    if (raw.startsWith('en')) return 'en'
  } catch (error) {
    console.error(
      '[soykajs] не удалось прочитать ltms-locale из localStorage',
      error
    )
  }
  return 'ru'
}
const userReady = computed(
  () => Boolean(user.value?.id && user.value?.access_token)
)
let instance
/** Path, уже найденный в goToViewer (чтобы leave-guard не дублировал poll). */
let pendingViewerFilename = null
/** Идёт resolveViewerFilename — не запускать второй poll из leave-guard. */
let resolvingViewer = false
/** OnlyOffice: документ меняли в этой сессии (onDocumentStateChange). */
let editorWasDirty = false
/** Сейчас есть несохранённые правки (data===true); false — save дошёл до callback. */
let editorIsDirty = false
/** Колбэки waitUntilEditorSaved — срабатывают при data===false. */
let editorCleanWaiters = []
/** Уже ушли из редактора, потому что нет пароля — не дёргать goToViewer повторно. */
let leftLockedEditor = false
/** Один уход за раз: второй back отменяется vue-router и возвращает в редактор. */
let leavingEditor = false
/** Latest по dockey на момент открытия редактора — чтобы отличить «был save» от чужой актуальной версии. */
let baselineLatestFilename = null
/** Уже показали предупреждение про правку неактуальной версии. */
let staleVersionWarned = false
/** Модалка: правка неактуальной версии → Save создаст новую актуальную. */
const staleVersionWarningVisible = ref(false)
/** Оверлей: ждём окончания Save перед возвратом в просмотрщик. */
const savingBeforeLeave = ref(false)
const docInfo = reactive({})
/** Открываем выбранную в просмотрщике версию (path), не latest по dockey. */
const docInfoUrl = computed(() => {
  const service = defaultConfig.service || ''
  const from = fromFilename.value
  if (from) {
    return `docs:info?path=${encodeURIComponent(from)}&service=${service}`
  }
  return `docs:info?key=${props.dockey}&service=${service}`
})

const staleVersionWarningText = computed(() => {
  if (editorUiLang() === 'en') {
    return {
      title: 'Non-current version',
      body:
        'A non-current version was edited. Saving the document will create a new current version.',
      ok: 'OK',
    }
  }
  return {
    title: 'Неактуальная версия',
    body:
      'Отредактирована неактуальная версия. При сохранении документа сформируется новая актуальная версия',
    ok: 'OK',
  }
})

const savingBeforeLeaveText = computed(() =>
  editorUiLang() === 'en'
    ? 'Saving document…'
    : 'Сохранение документа…'
)

const isEditable = ref(false)

watch([() => props.editable, docInfo], ([editable, docInfo]) => {
  if (!editable) {
    isEditable.value = false
    return
  }

  if (!docInfo.secure_filename) {
    isEditable.value = false
    return
  }

  isEditable.value = checkIfEditable(docInfo)
})

useApi({
  url: docInfoUrl,
  immediate: true,
  model: docInfo,
  watch: [docInfoUrl],
  before: () => props.editable,
  after: () => {
    // Зафиксировать «актуальную» на момент входа — для возврата после save
    void captureBaselineLatest()
  },
})

const { model: users, refresh: getUsers } = useApi({
  url: 'users',
  data: [],
  immediate: false,
})

const { refresh: sendMentionNotify } = useApi({
  method: 'POST',
  url: 'doc-mention',
  immediate: false,
})
extend(window, { extensionParams: { url: `${defaultConfig.url_r7}/web-apps/` } })

function buildEditorPermissions(canEdit) {
  return {
    edit: canEdit,
    download: true,
    print: true,
    comment: canEdit,
    review: canEdit,
    fillForms: canEdit,
    copy: true,
    chat: canEdit,
    // Вкладка «Защита»: лист, диапазон ячеек, блокировка ячеек.
    // Без этого флага в xlsx остаётся только пароль на весь файл.
    protect: canEdit,
  }
}

function buildEditorUser(canEdit) {
  return {
    id: String(user.value.id),
    name: user.value.fio || user.value.username,
    email: user.value.email_notifications,
    group: '',
    permissions: buildEditorPermissions(canEdit),
  }
}

function listDirectoryUsers(canEdit) {
  const raw = users.value
  const list = Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw)
      ? raw
      : []
  const mapped = list.map(u => ({
    id: String(u.id),
    email: u.email_notifications,
    name: u.fio || u.username,
    group: '',
    permissions: buildEditorPermissions(canEdit),
  }))
  const current = buildEditorUser(canEdit)
  if (!mapped.some(u => u.id === current.id)) {
    mapped.unshift(current)
  }
  return mapped
}

function registerEditorUsers(canEdit, command = 'info') {
  instance?.setUsers?.({
    c: command,
    users: listDirectoryUsers(canEdit),
  })
}

async function fetchDockeyLatestFilename() {
  const info = await fetchDockeyInfo()
  return info?.secure_filename || null
}

/**
 * Полный /info по dockey (latest + versions) — чтобы увидеть path после R7 save.
 *
 * @returns {Promise<object|null>}
 */
async function fetchDockeyInfo() {
  const key = props.dockey
  const token = user.value?.access_token
  if (!key || !token || !defaultConfig.url_docparse) {
    return null
  }
  const url = new URL('info', defaultConfig.url_docparse)
  url.searchParams.set('key', String(key))
  url.searchParams.set('service', String(defaultConfig.service || ''))
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  })
  if (!resp.ok) {
    return null
  }
  return resp.json()
}

async function captureBaselineLatest() {
  if (baselineLatestFilename) {
    return
  }
  try {
    baselineLatestFilename = (await fetchDockeyLatestFilename()) || fromFilename.value || ''
  } catch (error) {
    console.error(
      '[soykajs] не удалось получить latest-файл по dockey',
      error
    )
    baselineLatestFilename = fromFilename.value || docInfo.secure_filename || ''
  }
}

/**
 * Редактируем не latest по dockey (в т.ч. soft-deleted archive row).
 * @returns {boolean}
 */
function isEditingNonCurrentVersion() {
  const opened = fromFilename.value || docInfo.secure_filename || ''
  if (!opened) {
    return false
  }
  const latest = baselineLatestFilename || ''
  if (latest && opened !== latest) {
    return true
  }
  const versions = Array.isArray(docInfo.versions) ? docInfo.versions : []
  const self = versions.find(v => v?.secure_filename === opened)
  if (self?.deleted) {
    return true
  }
  if (versions.length > 1) {
    const live = versions.filter(v => v?.secure_filename && !v.deleted)
    const pool = live.length ? live : versions
    const maxVer = pool.reduce((best, row) => {
      const bv = Number(best?.version) || 0
      const rv = Number(row?.version) || 0
      if (rv !== bv) {
        return rv > bv ? row : best
      }
      return String(row?.uploaded_at || '') > String(best?.uploaded_at || '') ? row : best
    }, null)
    if (maxVer?.secure_filename && maxVer.secure_filename !== opened) {
      return true
    }
  }
  return false
}

/**
 * Один раз за сессию редактора: правки неактуальной версии → Save создаст новую.
 */
async function maybeWarnStaleVersion() {
  if (staleVersionWarned || staleVersionWarningVisible.value) {
    return
  }
  await captureBaselineLatest()
  if (!isEditingNonCurrentVersion()) {
    return
  }
  staleVersionWarned = true
  staleVersionWarningVisible.value = true
}

function dismissStaleVersionWarning() {
  staleVersionWarningVisible.value = false
}

/**
 * Есть ли в /info новый path относительно baseline / момента открытия.
 *
 * @param {object|null} info
 * @param {string} opened
 * @param {string} baseline
 * @param {number} openedAt
 * @returns {string|null}
 */
function pickSavedFilename(info, opened, baseline, openedAt) {
  const latest = info?.secure_filename || null
  if (latest && latest !== baseline) {
    return latest
  }
  const fresh = (info?.versions || [])
    .filter(v => v?.secure_filename && v.secure_filename !== opened)
    .filter(v => {
      const t = Date.parse(v.uploaded_at || '')
      return Number.isFinite(t) && t >= openedAt - 5000
    })
    .sort((a, b) =>
      String(b.uploaded_at || '').localeCompare(String(a.uploaded_at || ''))
    )[0]
  return fresh?.secure_filename || null
}

/**
 * onDocumentStateChange: true — есть правки; false — сохранено на сервер (ds-callback).
 *
 * @param {boolean} dirty
 */
function setEditorDirty(dirty) {
  editorIsDirty = Boolean(dirty)
  if (editorIsDirty) {
    editorWasDirty = true
    return
  }
  const waiters = editorCleanWaiters.splice(0, editorCleanWaiters.length)
  for (const notify of waiters) {
    notify()
  }
}

/**
 * Ждём clean (onDocumentStateChange false).
 * @param {number} timeoutMs
 * @param {boolean} requireDirtyFirst ждать dirty→clean (не текущий clean после autosave)
 * @returns {Promise<boolean>}
 */
function waitUntilEditorSaved(timeoutMs, requireDirtyFirst) {
  if (!editorIsDirty && !requireDirtyFirst) {
    return Promise.resolve(true)
  }
  return new Promise(resolve => {
    let settled = false
    let sawDirty = editorIsDirty
    const finish = ok => {
      if (settled) {
        return
      }
      settled = true
      window.clearTimeout(timer)
      window.clearInterval(dirtyPoll)
      const idx = editorCleanWaiters.indexOf(onClean)
      if (idx >= 0) {
        editorCleanWaiters.splice(idx, 1)
      }
      resolve(ok)
    }
    const onClean = () => {
      if (requireDirtyFirst && !sawDirty) {
        // setEditorDirty снял waiter из массива — вернём подписку.
        editorCleanWaiters.push(onClean)
        return
      }
      finish(true)
    }
    const timer = window.setTimeout(() => finish(false), timeoutMs)
    const dirtyPoll = window.setInterval(() => {
      if (editorIsDirty) {
        sawDirty = true
      }
    }, 50)
    editorCleanWaiters.push(onClean)
  })
}

function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

/**
 * Перед уходом: не рвать iframe, дождаться окончания Save.
 * Даже если autosave уже снял dirty — кнопка Save могла запустить ещё один
 * forcesave; при повторном сеансе как раз этот случай «сразу назад».
 *
 * @returns {Promise<void>}
 */
async function ensureSavedBeforeLeave() {
  if (!instance) {
    return
  }
  if (!editorIsDirty && !editorWasDirty) {
    return
  }
  savingBeforeLeave.value = true
  try {
    const needsFreshCycle = editorWasDirty && !editorIsDirty
    try {
      // R7/OO internalCommand: принудительный save при customization.forcesave.
      instance.serviceCommand?.('forcesave')
    } catch (error) {
      console.error('[soykajs] не удалось вызвать forcesave перед уходом', error)
    }
    if (editorIsDirty) {
      await waitUntilEditorSaved(90000, false)
    } else if (needsFreshCycle) {
      // Уже clean после autosave: ждём dirty→clean от Save/forcesave.
      // Если R7 не шлёт dirty на повторный Save — не висеть долго (settle).
      await Promise.race([
        waitUntilEditorSaved(30000, true),
        // Если повторный Save не поднимает dirty — дать дойти in-flight callback.
        sleep(8000),
      ])
    }
  } finally {
    savingBeforeLeave.value = false
  }
}

/**
 * Path для возврата в просмотрщик.
 * - без save → выбранная версия (`from`);
 * - после R7 save → новый path (latest по dockey сменился относительно baseline);
 * - in-place черновик (тот же path) — opened, контент уже на S3 после ensureSavedBeforeLeave.
 *
 * @param {{ waitForSave?: boolean }} [opts]
 * @returns {Promise<string>}
 */
async function resolveViewerFilename(opts = {}) {
  const waitForSave = Boolean(opts.waitForSave)
  const opened = fromFilename.value || docInfo.secure_filename || ''
  if (!opened) {
    return (await fetchDockeyLatestFilename()) || ''
  }

  if (!waitForSave) {
    return opened
  }

  await captureBaselineLatest()
  const baseline = baselineLatestFilename || opened
  const openedAt = Date.now()

  // Без правок — один check (тихий save), без спама info?key= ~14 раз / 5с.
  if (!editorWasDirty) {
    try {
      const info = await fetchDockeyInfo()
      const saved = pickSavedFilename(info, opened, baseline, openedAt)
      if (saved) {
        return saved
      }
    } catch (error) {
      console.error(
        '[soykajs] не удалось получить info документа при возврате в просмотрщик',
        error
      )
    }
    return opened
  }

  // Save уже дождались в ensureSavedBeforeLeave; короткий poll на новый path (v2+).
  const deadline = Date.now() + 5000
  while (Date.now() < deadline) {
    let info = null
    try {
      info = await fetchDockeyInfo()
    } catch (error) {
      console.error(
        '[soykajs] не удалось получить info документа после сохранения',
        error
      )
      info = null
    }
    const saved = pickSavedFilename(info, opened, baseline, openedAt)
    if (saved) {
      return saved
    }
    await new Promise(resolve => setTimeout(resolve, 400))
  }

  // Тот же path (черновик create_file / overwrite) — актуальные байты уже на сервере.
  return opened
}

/**
 * Сбросить leave-флаг. Раньше при уходе на /docview флаг оставался true —
 * при повторном Back goToViewer сразу выходил, R7 уходил по goback.url без ожидания Save.
 */
function releaseLeaveIfStillEditing() {
  window.setTimeout(() => {
    leavingEditor = false
  }, 0)
}

/** Сброс сессионных флагов при каждом (пере)открытии редактора. */
function resetEditorSessionState() {
  leavingEditor = false
  resolvingViewer = false
  pendingViewerFilename = null
  editorWasDirty = false
  editorIsDirty = false
  editorCleanWaiters = []
  baselineLatestFilename = null
  staleVersionWarned = false
  leftLockedEditor = false
  savingBeforeLeave.value = false
}

/**
 * Возврат в просмотрщик на ту же версию (или на результат save).
 * Один переход: повторный back от R7 отменяется и снова открывает редактор.
 * Сначала ждём окончания Save — иначе destroyEditor рвёт ds-callback.
 */
async function goToViewer() {
  if (leavingEditor || resolvingViewer) {
    return
  }
  leavingEditor = true
  try {
    await ensureSavedBeforeLeave()
  } catch (error) {
    console.error('[soykajs] ошибка ожидания сохранения перед уходом', error)
  }

  markEditorLeft(props.dockey)
  try {
    instance?.destroyEditor()
  } catch (error) {
    console.error('[soykajs] не удалось уничтожить экземпляр редактора', error)
  }
  instance = null

  resolvingViewer = true
  let filename = ''
  try {
    filename = await resolveViewerFilename({ waitForSave: true })
  } finally {
    resolvingViewer = false
  }
  pendingViewerFilename = filename
  if (!filename) {
    pendingViewerFilename = null
    try {
      await router.push('/')
    } catch (error) {
      console.error(
        '[soykajs] не удалось перейти на главную после закрытия редактора',
        error
      )
    } finally {
      releaseLeaveIfStillEditing()
    }
    return
  }

  const viewerQuery = {
    _: String(Date.now()),
    ...(props.dockey ? { dockey: String(props.dockey) } : {}),
  }

  try {
    if (router.options.history.state.back) {
      const removeHook = router.afterEach(to => {
        removeHook()
        pendingViewerFilename = null
        // Back на /docview — обновить path/версию. Иначе (карточка/досье) —
        // всё равно открыть просмотрщик той версии, что была в редакторе.
        if (to.name === 'soykajs.docview') {
          router.replace({
            name: 'soykajs.docview',
            params: { filename },
            query: viewerQuery,
          })
          return
        }
        router.push({
          name: 'soykajs.docview',
          params: { filename },
          query: viewerQuery,
        })
      })
      await router.back()
      return
    }

    pendingViewerFilename = null
    await router.replace({
      name: 'soykajs.docview',
      params: { filename },
      query: viewerQuery,
    })
  } catch (error) {
    console.error(
      '[soykajs] не удалось перейти в просмотрщик из редактора',
      error
    )
  } finally {
    releaseLeaveIfStillEditing()
  }
}

/**
 * Абсолютный URL просмотрщика для goback.url (fallback, если requestClose недоступен).
 *
 * @returns {string}
 */
function viewerAbsoluteUrl() {
  const filename = fromFilename.value || docInfo.secure_filename
  if (!filename) {
    return window.location.origin + '/'
  }
  const resolved = router.resolve({
    name: 'soykajs.docview',
    params: { filename },
  })
  return new URL(resolved.href, window.location.origin).href
}

/** Отмена устаревшего async-init редактора при быстрой смене docInfo. */
let editorInitGeneration = 0

watch([docInfo, isEditable, userReady, unlockToken], async ([docInfo, isEditable, userReady]) => {
  if (instance) {
    instance.destroyEditor()
    instance = null
  }
  resetEditorSessionState()

  if (!isEditable || !userReady) {
    return
  }

  let docparser = defaultConfig.url_docparse
  const ec = getEditorConfig(docInfo)
  if (!ec) {
    return
  }

  // Без пароля в редактор не входим: /download отдал бы restricted.pdf,
  // и Р7 крутит спиннер, пытаясь открыть его как xlsx/docx.
  if (docInfo.restricted && !unlockToken.value) {
    if (!leftLockedEditor) {
      leftLockedEditor = true
      void goToViewer()
    }
    return
  }
  leftLockedEditor = false

  const canEdit = true
  const unlockParam = unlockToken.value
    ? `&unlock=${encodeURIComponent(unlockToken.value)}`
    : ''

  const initGen = ++editorInitGeneration
  await captureBaselineLatest()
  if (initGen !== editorInitGeneration) {
    return
  }

  // key = editor_key || id версии (docparser Uploads.sync_editor_key).
  // Не dockey: после закрытия сессии с сохранением Р7 ~5 мин держит ключ в
  // UpdateVersion — тот же ключ даёт «Версия файла была изменена».
  // editor_key — ключ живой сессии после forcesave (версия уже новая), чтобы
  // второй редактор попал в ту же co-edit сессию.
  // Для правки архивной версии — изолированный key по id upload.
  const openedPath = fromFilename.value || docInfo.secure_filename || ''
  const latestPath = baselineLatestFilename || docInfo.secure_filename || ''
  const editingArchive =
    Boolean(openedPath && latestPath && openedPath !== latestPath)
  const documentKey = editingArchive
    ? `${docInfo.id}.p1`
    : String(docInfo.editor_key || docInfo.id)

  instance = new DocsAPI.DocEditor('ed', {
    documentType: ec.documentType,
    document: {
      fileType: ec.fileType,
      url: `${docparser}download?id=${docInfo.id}&auth=${user.value.access_token}&service=${encodeURIComponent(defaultConfig.service || '')}${unlockParam}`,
      key: documentKey,
      title: docInfo.filename,
      permissions: buildEditorPermissions(canEdit),
    },
    editorConfig: {
      mode: canEdit ? 'edit' : 'view',
      callbackUrl: `${docparser}ds-callback?auth=${user.value.access_token}&service=${defaultConfig.service}`,
      createUrl: `${docparser}create-file?auth=${user.value.access_token}&service=${defaultConfig.service}`,
      user: buildEditorUser(canEdit),
      coEditing: {
        // Fast — стандартный R7: правки второго редактора видны в реальном времени.
        // (strict + autosave:false ломает live co-edit: чужие правки только после Save.)
        mode: 'fast',
        change: true,
      },
      customization: {
        // autosave обязателен для fast: при false R7 принудительно уходит в strict.
        autosave: true,
        forcesave: true,
        features: {
          roles: false,
        },
        goback: {
          blank: false,
          text: 'Вернуться в просмотрщик',
          // Fallback только если onRequestClose недоступен; основной путь — goToViewer.
          url: viewerAbsoluteUrl(),
          requestClose: true,
        },
      },
      lang: editorUiLang(),
    },
    width: '100%',
    height: '100%',
    events: {
      onAppReady() {
        registerEditorUsers(canEdit)
      },
      onDocumentReady() {
        registerEditorUsers(canEdit)
      },
      onDocumentStateChange(event) {
        // OnlyOffice: data === true — изменён; false — сохранён (callback ок).
        const dirty = event?.data === true
        setEditorDirty(dirty)
        if (dirty && editingArchive) {
          // Только при правке архивной версии (не во время co-edit актуальной).
          void maybeWarnStaleVersion()
        }
      },
      onRequestClose() {
        void goToViewer()
      },
      onRequestUsers(e) {
        ;(async () => {
          if (!Array.isArray(users.value?.data) && !users.value?.length) {
            await getUsers()
          }

          instance.setUsers({
            c: e.data.c,
            users: listDirectoryUsers(canEdit),
          })
        })()
      },
      async onRequestSendNotify(e) {
        const recipients = e.data.emails.map(
          email => users.value.data.find(user => user.email_notifications == email).id
        )

        await sendMentionNotify({
          data: {
            dockey: props.dockey,
            recipients,
            message: e.data.message,
            ref_link: `/edit/${props.dockey}/${e.data.actionLink.action.type}_${e.data.actionLink.action.data}`,
          },
        })
      },
    },
  })
})

onBeforeRouteLeave(async to => {
  // goToViewer сам сделает replace через afterEach
  if (pendingViewerFilename || resolvingViewer) {
    return
  }
  // Пока ждём Save в goToViewer — не уходить по Browser Back (иначе сорвём callback).
  if (leavingEditor && savingBeforeLeave.value) {
    return false
  }
  if (leavingEditor) {
    return
  }

  try {
    await ensureSavedBeforeLeave()
  } catch (error) {
    console.error(
      '[soykajs] ошибка ожидания сохранения при уходе с маршрута',
      error
    )
  }

  markEditorLeft(props.dockey)
  try {
    instance?.destroyEditor()
  } catch (error) {
    console.error(
      '[soykajs] не удалось уничтожить экземпляр редактора при выходе с маршрута',
      error
    )
  }
  instance = null

  if (to.name !== 'soykajs.docview') {
    return
  }

  // Browser Back: та же версия / результат save — не прыгать на чужой latest
  resolvingViewer = true
  let target = ''
  try {
    target = await resolveViewerFilename({ waitForSave: true })
  } finally {
    resolvingViewer = false
  }
  if (!target) {
    return
  }
  if (String(to.params.filename) === String(target) && to.query?._) {
    return
  }
  return {
    name: 'soykajs.docview',
    params: { filename: target },
    query: {
      _: String(Date.now()),
      ...(props.dockey ? { dockey: String(props.dockey) } : {}),
    },
    replace: true,
  }
})
</script>

<template>
  <div class="docview position-relative">
    <div id="ed" v-show="isEditable"></div>
    <div v-if="!isEditable" class="alert alert-info">
      <i class="bi bi-exclamation-triangle-fill text-warning"></i>
      Редактирование недоступно
    </div>

    <div
      v-if="savingBeforeLeave"
      class="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
      style="z-index: 20; background: rgba(255, 255, 255, 0.85)"
    >
      <div class="spinner-border text-primary" role="status" aria-hidden="true" />
      <div class="mt-3">{{ savingBeforeLeaveText }}</div>
    </div>

    <Teleport to="body">
      <div
        v-if="staleVersionWarningVisible"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        style="z-index: 10050"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ staleVersionWarningText.title }}</h5>
              <button
                type="button"
                class="btn-close"
                :aria-label="staleVersionWarningText.ok"
                @click="dismissStaleVersionWarning"
              />
            </div>
            <div class="modal-body">
              <div class="d-flex gap-3 align-items-start">
                <i
                  class="bi bi-exclamation-triangle-fill text-warning fs-3 flex-shrink-0"
                  aria-hidden="true"
                />
                <p class="mb-0">{{ staleVersionWarningText.body }}</p>
              </div>
            </div>
            <div class="modal-footer justify-content-center">
              <button
                type="button"
                class="btn btn-secondary"
                @click="dismissStaleVersionWarning"
              >
                {{ staleVersionWarningText.ok }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="staleVersionWarningVisible"
        class="modal-backdrop fade show"
        style="z-index: 10040"
      />
    </Teleport>
  </div>
</template>
